import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { schemaApi } from '@/services/api';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PromptSection — A labeled textarea with optional badge and help text.
 */
function PromptSection({ label, badge, help, value, onChange, rows = 4, placeholder }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        {badge && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {badge}
          </Badge>
        )}
      </div>
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="resize-y text-sm font-mono text-xs leading-relaxed"
      />
    </div>
  );
}

/**
 * CuriosityPromptsTab — App-owner editable GOAL + DATA DICTIONARY.
 * ROLE and GUIDELINES are admin-controlled on the Service Catalog.
 */
export default function CuriosityPromptsTab({ schema, onChange }) {
  const { appId } = useParams();
  const [generating, setGenerating] = useState(false);

  // Read from subscription app_prompt, fall back to legacy curiosity_prompts
  const subscriptions = schema?.subscriptions || {};
  const systemSubs = subscriptions.system || [];
  const curiositySub = systemSubs.find(s => s.serviceId === 'system:curiosity');
  const appPrompt = curiositySub?.app_prompt || {
    goal: schema?.curiosity_prompts?.external?.goal || '',
    data_dictionary: schema?.curiosity_prompts?.external?.data_dictionary || '',
  };

  const hasPrompt = appPrompt.goal || appPrompt.data_dictionary;

  function updateAppPrompt(updated) {
    const newSystemSubs = systemSubs.map(s =>
      s.serviceId === 'system:curiosity'
        ? { ...s, app_prompt: updated }
        : s
    );
    // If no curiosity sub exists, add one
    if (!curiositySub) {
      newSystemSubs.push({ serviceId: 'system:curiosity', enabled: true, app_prompt: updated });
    }
    onChange({
      ...schema,
      subscriptions: { ...subscriptions, system: newSystemSubs },
      // Also update legacy field for backward compat
      curiosity_prompts: {
        ...schema?.curiosity_prompts,
        external: { goal: updated.goal, data_dictionary: updated.data_dictionary },
      },
    });
  }

  async function handleGenerate() {
    try {
      setGenerating(true);
      const res = await schemaApi.generateCuriosityPrompts(appId);
      const newAppPrompt = res.data?.data?.app_prompt;
      if (newAppPrompt) {
        updateAppPrompt(newAppPrompt);
        toast.success('App voice generated');
      }
    } catch (err) {
      console.error('Generate app prompt failed:', err);
      toast.error(err.response?.data?.error || 'Failed to generate app voice');
    } finally {
      setGenerating(false);
    }
  }

  // Empty state
  if (!hasPrompt) {
    return (
      <div className="py-12 text-center space-y-4">
        <Sparkles className="h-10 w-10 mx-auto text-muted-foreground/50" />
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">No app voice configured</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            Generate per-app prompts that shape how AI services talk to your users.
            The GOAL and DATA DICTIONARY are derived from your app description and enabled services.
          </p>
        </div>
        <Button onClick={handleGenerate} disabled={generating}>
          {generating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate App Voice
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            The Goal and Data Dictionary shape how AI services communicate in your app's domain language.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating}>
          {generating ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
          )}
          Regenerate
        </Button>
      </div>

      {/* App Voice Card */}
      <Card>
        <CardContent className="p-5 space-y-5">
          <PromptSection
            label="Goal"
            badge="Auto-generated"
            help="The mission and context for AI services when working with this app. Regenerates from your app description + enabled services."
            value={appPrompt.goal}
            onChange={(v) => updateAppPrompt({ ...appPrompt, goal: v })}
            rows={6}
            placeholder="Click 'Generate App Voice' to populate this section..."
          />

          <PromptSection
            label="Data Dictionary"
            badge="Auto-generated"
            help="Maps graph terminology to user-facing language. Each line: GRAPH_TERM -> human translation."
            value={appPrompt.data_dictionary}
            onChange={(v) => updateAppPrompt({ ...appPrompt, data_dictionary: v })}
            rows={8}
            placeholder="RESONATES_WITH -> deeply connects with&#10;ACTIVATES -> stirs or evokes&#10;ContentAtom -> content&#10;..."
          />
        </CardContent>
      </Card>

      {/* Admin notice */}
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-muted/50 border border-muted">
        <ShieldCheck className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Safety constraints (Role and Guidelines) are managed by your platform admin on the{' '}
          <span className="text-foreground font-medium">Service Catalog</span>.
        </p>
      </div>
    </div>
  );
}
